import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const StudentVerification = ({ data, onUpdate, onNext }) => {
  const [formData, setFormData] = useState({
    fullName: data.fullName || '',
    studentId: data.studentId || '',
    universityEmail: data.universityEmail || '',
    faculty: data.faculty || '',
    yearOfStudy: data.yearOfStudy || '',
    program: data.program || '',
    studentIdDocument: data.studentIdDocument || null,
    admissionLetter: data.admissionLetter || null,
    verificationStatus: data.verificationStatus || 'pending'
  });

  const [errors, setErrors] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (field, file) => {
    if (file && file.size > 5 * 1024 * 1024) { // 5MB limit
      setErrors(prev => ({
        ...prev,
        [field]: 'File size must be less than 5MB'
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: file
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.studentId.trim()) {
      newErrors.studentId = 'Student ID is required';
    }

    if (!formData.universityEmail.trim()) {
      newErrors.universityEmail = 'University email is required';
    } else if (!formData.universityEmail.endsWith('.ac.ug')) {
      newErrors.universityEmail = 'Please use your university email (.ac.ug)';
    }

    if (!formData.faculty) {
      newErrors.faculty = 'Faculty selection is required';
    }

    if (!formData.yearOfStudy) {
      newErrors.yearOfStudy = 'Year of study is required';
    }

    if (!formData.program.trim()) {
      newErrors.program = 'Program of study is required';
    }

    if (!formData.studentIdDocument) {
      newErrors.studentIdDocument = 'Student ID document is required';
    }

    if (!formData.admissionLetter) {
      newErrors.admissionLetter = 'Admission letter is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerify = async () => {
    if (!validateForm()) return;

    setIsVerifying(true);

    try {
      // Simulate verification process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock verification logic
      const isValid = formData.universityEmail.endsWith('.ac.ug') &&
                     formData.studentId.length >= 8;

      if (isValid) {
        setFormData(prev => ({
          ...prev,
          verificationStatus: 'verified'
        }));
        onUpdate(formData);
        onNext();
      } else {
        setErrors({
          general: 'Verification failed. Please check your information and try again.'
        });
      }
    } catch (error) {
      setErrors({
        general: 'Verification failed. Please try again later.'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const faculties = [
    'College of Agricultural and Environmental Sciences',
    'College of Business and Management Sciences',
    'College of Computing and Information Sciences',
    'College of Education and External Studies',
    'College of Engineering, Design, Art and Technology',
    'College of Health Sciences',
    'College of Humanities and Social Sciences',
    'College of Natural Sciences',
    'College of Veterinary Medicine, Animal Resources and Biosecurity',
    'School of Law'
  ];

  const yearsOfStudy = [
    'Year 1',
    'Year 2',
    'Year 3',
    'Year 4',
    'Year 5',
    'Masters Year 1',
    'Masters Year 2',
    'PhD'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Student Verification</h2>
        <p className="text-muted-foreground">
          To book university accommodation, you must be a registered student. Please provide your details for verification.
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{errors.general}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Personal Information</h3>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Full Name (as per university records) *
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.fullName ? 'border-red-500' : 'border-border'
              }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Student ID Number *
            </label>
            <input
              type="text"
              value={formData.studentId}
              onChange={(e) => handleInputChange('studentId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.studentId ? 'border-red-500' : 'border-border'
              }`}
              placeholder="e.g., 2000100001"
            />
            {errors.studentId && (
              <p className="text-red-500 text-xs mt-1">{errors.studentId}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              University Email Address *
            </label>
            <input
              type="email"
              value={formData.universityEmail}
              onChange={(e) => handleInputChange('universityEmail', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.universityEmail ? 'border-red-500' : 'border-border'
              }`}
              placeholder="your.name@mak.ac.ug"
            />
            {errors.universityEmail && (
              <p className="text-red-500 text-xs mt-1">{errors.universityEmail}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Must be your official university email ending in .ac.ug
            </p>
          </div>
        </div>

        {/* Academic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Academic Information</h3>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Faculty/College *
            </label>
            <select
              value={formData.faculty}
              onChange={(e) => handleInputChange('faculty', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.faculty ? 'border-red-500' : 'border-border'
              }`}
            >
              <option value="">Select your faculty</option>
              {faculties.map((faculty) => (
                <option key={faculty} value={faculty}>{faculty}</option>
              ))}
            </select>
            {errors.faculty && (
              <p className="text-red-500 text-xs mt-1">{errors.faculty}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Year of Study *
            </label>
            <select
              value={formData.yearOfStudy}
              onChange={(e) => handleInputChange('yearOfStudy', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.yearOfStudy ? 'border-red-500' : 'border-border'
              }`}
            >
              <option value="">Select year of study</option>
              {yearsOfStudy.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {errors.yearOfStudy && (
              <p className="text-red-500 text-xs mt-1">{errors.yearOfStudy}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Program of Study *
            </label>
            <input
              type="text"
              value={formData.program}
              onChange={(e) => handleInputChange('program', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.program ? 'border-red-500' : 'border-border'
              }`}
              placeholder="e.g., Bachelor of Science in Computer Science"
            />
            {errors.program && (
              <p className="text-red-500 text-xs mt-1">{errors.program}</p>
            )}
          </div>
        </div>
      </div>

      {/* Document Upload */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Document Verification</h3>
        <p className="text-muted-foreground text-sm">
          Please upload clear, readable copies of your documents. Maximum file size: 5MB each.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Student ID Document */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Student ID Card/Document *
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileUpload('studentIdDocument', e.target.files[0])}
                className="hidden"
                id="studentId"
              />
              <label htmlFor="studentId" className="cursor-pointer">
                <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {formData.studentIdDocument ? formData.studentIdDocument.name : 'Click to upload Student ID'}
                </p>
              </label>
            </div>
            {errors.studentIdDocument && (
              <p className="text-red-500 text-xs mt-1">{errors.studentIdDocument}</p>
            )}
          </div>

          {/* Admission Letter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              University Admission Letter *
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileUpload('admissionLetter', e.target.files[0])}
                className="hidden"
                id="admissionLetter"
              />
              <label htmlFor="admissionLetter" className="cursor-pointer">
                <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {formData.admissionLetter ? formData.admissionLetter.name : 'Click to upload Admission Letter'}
                </p>
              </label>
            </div>
            {errors.admissionLetter && (
              <p className="text-red-500 text-xs mt-1">{errors.admissionLetter}</p>
            )}
          </div>
        </div>
      </div>

      {/* Verification Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Info" size={20} className="text-blue-500 mr-3 mt-0.5" />
          <div>
            <h4 className="text-blue-800 font-medium">Verification Process</h4>
            <p className="text-blue-700 text-sm mt-1">
              Your documents will be verified by the university hostel administration.
              This process usually takes 1-2 business days. You'll receive a confirmation email once verified.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end pt-6">
        <Button
          onClick={handleVerify}
          disabled={isVerifying}
          className="min-w-[150px]"
        >
          {isVerifying ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Verifying...
            </>
          ) : (
            <>
              <Icon name="CheckCircle" size={16} className="mr-2" />
              Verify & Continue
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StudentVerification;